import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChartNode, StrukturOrganisasiPublicChart } from './api';
import {
  GARIS_DEFAULT, dashArrayFor, LABEL_JENIS, URUTAN_JENIS,
  buildLayoutMap, computeBounds, edgePath, findRootDetailId, hasConfiguredLayout, isKepalaDesa,
  type NodeLayout,
} from './org-chart';

export interface OrgChartRenderNodeArgs {
  node: ChartNode;
  layout: NodeLayout;
  showFoto: boolean;
  showPeriode: boolean;
}

export interface OrgChartCanvasProps {
  data: StrukturOrganisasiPublicChart;
  renderNode: (args: OrgChartRenderNodeArgs) => React.ReactNode;
  height?: number | string;
  background?: string;
  emptyText?: string;
  /** Override struktur.tampilkanLegenda if set. */
  showLegend?: boolean;
}

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;

const btn: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 8, border: '1px solid #cbd5e1',
  background: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: '30px', color: '#334155',
};

// Framework-agnostic, read-only chart. All chrome uses inline styles so it works
// in both Tailwind (aurora/breeze) and Mantine (verdant/zenith) templates.
export function OrgChartCanvas({
  data, renderNode, height = 600, background = '#f8fafc',
  emptyText = 'Bagan organisasi belum tersedia', showLegend,
}: OrgChartCanvasProps) {
  const { struktur, nodes, edges } = data;
  const layout = useMemo(() => buildLayoutMap(nodes), [nodes]);
  const bounds = useMemo(() => computeBounds(layout), [layout]);
  const configured = hasConfiguredLayout(nodes);

  const containerRef = useRef<HTMLDivElement>(null);
  const didCenterRef = useRef(false);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(24);
  const [ty, setTy] = useState(24);
  const panRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return; // touch/pen: let the page scroll normally
    panRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [tx, ty]);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const p = panRef.current; if (!p) return;
    setTx(p.tx + (e.clientX - p.x));
    setTy(p.ty + (e.clientY - p.y));
  }, []);
  // Shared by pointerup and pointercancel so an interrupted drag (mobile) can't
  // leave a stale pan baseline behind.
  const endPan = useCallback((e: React.PointerEvent) => {
    panRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
  }, []);

  // Center the view on the leader (pemimpin: kepala desa / pimpinan by name, else the
  // structural root) near the top-center of the canvas.
  const focusOnRoot = useCallback(() => {
    const el = containerRef.current;
    const cw = el ? el.clientWidth : 0;
    const leaderId = nodes.find(n => isKepalaDesa(n.namaJabatan))?.id ?? findRootDetailId(nodes, edges);
    const L = leaderId ? layout[leaderId] : undefined;
    setScale(1);
    if (L && cw > 0) {
      setTx(Math.round(cw / 2 - (L.x + L.w / 2)));
      setTy(Math.round(40 - L.y));
    } else {
      setTx(24);
      setTy(24);
    }
  }, [nodes, edges, layout]);

  // Auto-center on the leader the first time the chart is measurable. A ResizeObserver
  // covers the case where the container has no width yet on first commit (hidden tab,
  // layout/fonts not settled) so we don't lock in the top-left fallback.
  useLayoutEffect(() => {
    if (!configured) return;
    const el = containerRef.current;
    if (!el) return;
    const tryCenter = () => {
      if (didCenterRef.current || el.clientWidth === 0) return;
      focusOnRoot();
      didCenterRef.current = true;
    };
    tryCenter();
    if (didCenterRef.current) return;
    const ro = new ResizeObserver(tryCenter);
    ro.observe(el);
    return () => ro.disconnect();
  }, [configured, focusOnRoot]);

  if (!struktur || nodes.length === 0 || !configured) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background, borderRadius: 12, color: '#64748b', fontSize: 14, textAlign: 'center', padding: 24,
      }}>
        {emptyText}
      </div>
    );
  }

  const lineMode = struktur.modeGaris;
  const showFoto = struktur.tampilkanFoto;
  const showPeriode = struktur.tampilkanPeriode;
  const legendVisible = showLegend ?? struktur.tampilkanLegenda;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative', width: '100%', height, overflow: 'hidden',
        background, borderRadius: 12, userSelect: 'none',
      }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      onPointerUp={endPan} onPointerCancel={endPan}
    >
      <div
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 5, display: 'flex', gap: 6 }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button type="button" style={btn} onClick={() => setScale(s => Math.min(ZOOM_MAX, s + ZOOM_STEP))} aria-label="Perbesar">+</button>
        <button type="button" style={btn} onClick={() => setScale(s => Math.max(ZOOM_MIN, s - ZOOM_STEP))} aria-label="Perkecil">−</button>
        <button type="button" style={btn} onClick={focusOnRoot} aria-label="Reset tampilan">⟲</button>
      </div>

      <div style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: '0 0', position: 'absolute' }}>
        <svg
          width={bounds.w} height={bounds.h}
          style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible', pointerEvents: 'none', maxWidth: 'none' }}
        >
          {edges.map(e => {
            const s = layout[e.sourceDetailId], t = layout[e.targetDetailId];
            if (!s || !t) return null;
            const style = GARIS_DEFAULT[e.lineType];
            const color = e.lineColor || style.warna; // treat null/"" as "use default"
            return (
              <path
                key={e.relasiJabatanId}
                d={edgePath(s, t, lineMode, e.lineBendX ?? null, e.lineBendY ?? null)}
                fill="none" stroke={color} strokeDasharray={dashArrayFor(e.lineType)}
                strokeLinecap="round" style={{ strokeWidth: style.ketebalan }}
              />
            );
          })}
        </svg>

        {nodes.map(n => {
          const L = layout[n.id]; if (!L) return null;
          return (
            <div key={n.id} style={{ position: 'absolute', left: L.x, top: L.y, width: L.w, height: L.h }}>
              {renderNode({ node: n, layout: L, showFoto, showPeriode })}
            </div>
          );
        })}
      </div>

      {legendVisible && (
        <div style={{
          position: 'absolute', bottom: 12, left: 12, zIndex: 5, display: 'flex', flexWrap: 'wrap', gap: 12,
          background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px',
        }}>
          {URUTAN_JENIS.map(j => (
            <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
              <svg width={28} height={8} style={{ maxWidth: 'none', flexShrink: 0 }}>
                <line x1={0} y1={4} x2={28} y2={4} stroke={GARIS_DEFAULT[j].warna} strokeWidth={2} strokeDasharray={dashArrayFor(j)} />
              </svg>
              {LABEL_JENIS[j]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrgChartCanvas;
